/**
 * LandingRedirect — opens ROS to wherever the user last was.
 *
 * On every authenticated route change, useLastVisitedTracker writes the
 * pathname (+ search + hash) to localStorage. When the user later opens
 * ROS at "/" (or any path that bounces here), this component reads the
 * saved value and Navigates there. First-time users — or anyone whose
 * last path was a transient/auth route — land on /home.
 *
 * Tracker blocks: /, /login, /pending. Anything else is fair game.
 */
import { useEffect } from "react";
import { useLocation, Navigate } from "react-router-dom";

const LAST_PATH_KEY = "ros_last_visited_path_v1";

// Paths we intentionally don't restore the user TO (would loop or
// stick them in an auth-flow page). We do save other paths.
const NEVER_RESTORE_PREFIXES = ["/login", "/pending"];

function isRestorable(path) {
  if (!path || typeof path !== "string") return false;
  if (path === "/") return false;
  return !NEVER_RESTORE_PREFIXES.some((p) => path === p || path.startsWith(p + "/") || path.startsWith(p + "?"));
}

export function useLastVisitedTracker() {
  const location = useLocation();
  useEffect(() => {
    const full = location.pathname + location.search + location.hash;
    if (!isRestorable(location.pathname)) return;
    try {
      localStorage.setItem(LAST_PATH_KEY, full);
    } catch {
      // storage quota or disabled — silently degrade
    }
  }, [location.pathname, location.search, location.hash]);
}

// Feeds the admin usage board (active-now + peak-hours). Fire-and-forget:
// a failed/blocked request never affects navigation.
export function usePageViewTracker() {
  const location = useLocation();
  useEffect(() => {
    if (!isRestorable(location.pathname)) return;
    fetch("/api/activity/pageview", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ path: location.pathname }),
    }).catch(() => {});
  }, [location.pathname]);
}

export default function LandingRedirect() {
  let target = "/home";
  try {
    const saved = localStorage.getItem(LAST_PATH_KEY);
    if (saved && isRestorable(saved.split("?")[0].split("#")[0])) {
      target = saved;
    }
  } catch {
    // ignore
  }
  return <Navigate to={target} replace />;
}
